import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ThumbsUp, ThumbsDown, MessageSquare, Save } from 'lucide-react';
import translationChatAPI from '../../services/translationChatService';

// Compact inline 👍/👎 + optional comment control for one translation direction.
// Mirrors RatingControl's validation (comment required for a thumbs-down rating) but stays
// small enough to sit under a one-line translation subtitle.
const TranslationFeedbackControl = ({ direction, targetLanguage, threadId, turnIndex, agentVersion, sourceText, translatedText }) => {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [showComment, setShowComment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const submit = async (nextRating, nextComment) => {
    if (nextRating === false && !(nextComment || '').trim()) {
      setShowComment(true);
      setError('Comment required for a thumbs down rating');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await translationChatAPI.saveFeedback({
        thread_id: threadId,
        direction,
        turn_index: turnIndex,
        target_language: targetLanguage,
        agent_version: agentVersion,
        source_text: sourceText,
        translated_text: translatedText,
        rating: nextRating,
        comment: nextComment || '',
      });
      setSaved(true);
      setShowComment(false);
    } catch {
      setError('Failed to save feedback');
    } finally {
      setSaving(false);
    }
  };

  const handleThumb = (value) => {
    setRating(value);
    setSaved(false);
    setError('');
    if (value === false) {
      setShowComment(true);
    } else {
      submit(value, comment);
    }
  };

  return (
    <div className="mt-1">
      <div className="flex items-center gap-1">
        <button
          onClick={() => handleThumb(true)}
          className={`p-1 rounded transition-colors ${rating === true ? 'text-brand-green' : 'text-ink-mute hover:text-brand-green'}`}
        >
          <ThumbsUp className="h-3 w-3" />
        </button>
        <button
          onClick={() => handleThumb(false)}
          className={`p-1 rounded transition-colors ${rating === false ? 'text-negative' : 'text-ink-mute hover:text-negative'}`}
        >
          <ThumbsDown className="h-3 w-3" />
        </button>
        <button
          onClick={() => setShowComment((v) => !v)}
          className="p-1 rounded text-ink-mute hover:text-ink transition-colors"
        >
          <MessageSquare className="h-3 w-3" />
        </button>
        {saved && !showComment && <span className="text-[10px] text-ink-mute">saved</span>}
      </div>
      {showComment && (
        <div className="mt-1.5 w-64">
          <Textarea
            rows={2}
            className="text-xs"
            placeholder={rating === false ? 'Comment (required)' : 'Comment (optional)'}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          {error && <p className="text-[10px] text-negative mt-1">{error}</p>}
          <div className="flex justify-end gap-1 mt-1">
            <Button size="sm" variant="ghost" onClick={() => setShowComment(false)}>Close</Button>
            <Button size="sm" disabled={rating === null || saving} onClick={() => submit(rating, comment)} className="gap-1">
              <Save className="h-3 w-3" />Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranslationFeedbackControl;
